import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, CloudArrowUp, Trash, MapPin, Plug, PlugsConnected, CheckCircle, WifiSlash } from "@phosphor-icons/react";

// IndexedDB helpers (tiny, no library)
const DB_NAME = "span_offline_evidence";
const STORE = "queue";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function dbAll() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}
async function dbAdd(item) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).add(item);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function dbDelete(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export default function Capture() {
  const [online, setOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [note, setNote] = useState("");
  const [geo, setGeo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(null);
  const fileRef = useRef(null);

  const refreshQueue = async () => setQueue(await dbAll());

  useEffect(() => {
    refreshQueue();
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {}
      );
    }
    return () => { window.removeEventListener("online", onUp); window.removeEventListener("offline", onDown); };
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileMeta({ name: file.name, size: file.size, type: file.type });
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const enqueue = async () => {
    if (!preview || !fileMeta) return;
    await dbAdd({
      filename: fileMeta.name,
      data_uri: preview,
      note, case_id: caseId || null,
      captured_at: new Date().toISOString(),
      geo_lat: geo?.lat, geo_lng: geo?.lng,
    });
    setPreview(null); setFileMeta(null); setNote("");
    if (fileRef.current) fileRef.current.value = "";
    await refreshQueue();
  };

  const sync = async () => {
    if (!online || queue.length === 0) return;
    setSyncing(true); setSynced(null);
    try {
      const items = queue.map((q) => ({
        case_id: q.case_id, filename: q.filename, note: q.note,
        captured_at: q.captured_at, data_uri: q.data_uri,
        geo_lat: q.geo_lat, geo_lng: q.geo_lng,
      }));
      const { data } = await api.post("/capture/sync", items);
      // Delete the locally queued items that synced successfully.
      const failedNames = new Set((data.failed || []).map((f) => f.filename));
      for (const q of queue) {
        if (!failedNames.has(q.filename)) await dbDelete(q.id);
      }
      setSynced(data);
      await refreshQueue();
    } catch (e) {
      alert("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  const removeItem = async (id) => { await dbDelete(id); await refreshQueue(); };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="capture-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Field capture · PWA</div>
          <h1 className="font-display text-[22px] font-bold tracking-tight mt-1">Offline evidence queue</h1>
        </div>
        <div className={`px-2 py-1 text-[10px] font-mono uppercase flex items-center gap-1.5 ${online ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"}`} data-testid="online-status">
          {online ? <><PlugsConnected size={12}/> ONLINE</> : <><WifiSlash size={12}/> OFFLINE</>}
        </div>
      </div>

      <div className="card-flat p-4 space-y-3">
        <label className="label-eyebrow block">New capture</label>
        <div>
          <label className="text-[11px] block mb-1.5">Case (optional)</label>
          <input value={caseId} onChange={(e) => setCaseId(e.target.value)} placeholder="case_… or leave blank" className="span-input font-mono text-[12px]" data-testid="case-id-input"/>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="span-input" data-testid="capture-file"/>
        </div>
        {preview && (
          <div className="border border-[var(--span-border)]">
            <img src={preview} alt="preview" className="w-full max-h-64 object-cover"/>
          </div>
        )}
        <div>
          <label className="text-[11px] block mb-1.5">Note</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What is this evidence?" className="span-input" data-testid="capture-note"/>
        </div>
        {geo && (
          <div className="text-[10px] font-mono text-[var(--span-text-2)] flex items-center gap-1">
            <MapPin size={10}/> {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}
          </div>
        )}
        <button data-testid="enqueue-btn" onClick={enqueue} disabled={!preview} className="span-btn w-full">
          <Camera size={14}/> Save to queue
        </button>
      </div>

      <div className="card-flat">
        <div className="px-4 py-3 border-b border-[var(--span-border)] flex items-center justify-between">
          <div className="text-[12px]">
            <span className="font-mono">{queue.length}</span> in offline queue
          </div>
          <button data-testid="sync-btn" onClick={sync} disabled={!online || syncing || queue.length === 0} className="span-btn h-8 text-[11px]">
            <CloudArrowUp size={12}/> {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>
        {queue.length === 0 ? (
          <div className="p-6 text-center text-[12px] text-[var(--span-text-2)]">Queue is empty.</div>
        ) : (
          <ul className="divide-y divide-[var(--span-border)]">
            {queue.map((q) => (
              <li key={q.id} className="px-4 py-3 flex items-center gap-3" data-testid={`queue-item-${q.id}`}>
                {q.data_uri && <img src={q.data_uri} alt="thumb" className="w-12 h-12 object-cover border border-[var(--span-border)]"/>}
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium truncate">{q.filename}</div>
                  <div className="text-[10px] font-mono text-[var(--span-text-2)]">{q.case_id || "no-case"} · {new Date(q.captured_at).toLocaleString()}</div>
                  {q.note && <div className="text-[11px] mt-0.5 truncate">{q.note}</div>}
                </div>
                <button onClick={() => removeItem(q.id)} className="text-[var(--span-text-2)] hover:text-[var(--span-danger)]" data-testid={`remove-queue-${q.id}`}>
                  <Trash size={14}/>
                </button>
              </li>
            ))}
          </ul>
        )}
        {synced && (
          <div className="px-4 py-2 text-[11px] border-t border-[var(--span-border)] text-[var(--span-text-2)] flex items-center gap-1.5" data-testid="sync-result">
            <CheckCircle size={12} className="text-green-600"/> Synced {synced.synced?.length || 0}, failed {synced.failed?.length || 0}
          </div>
        )}
      </div>

      <div className="text-[10px] font-mono text-[var(--span-text-2)] text-center">
        STORED LOCALLY IN INDEXEDDB · UPLOADS RESUME AUTOMATICALLY WHEN ONLINE
      </div>
    </div>
  );
}
